import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './services/firebase';
import AdminLoginPage from './components/AdminLoginPage.tsx';
import AdminDashboard from './components/AdminDashboard.tsx';
import AdminSubscriptionDashboard from './components/AdminSubscriptionDashboard.tsx';
import AdminActivityDashboard from './components/AdminActivityDashboard.tsx';
import AdminUserManagement from './components/AdminUserManagement.tsx';
import AdminTeamManagement from './components/AdminTeamManagement.tsx';
import AdminRegistration from './components/AdminRegistration.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import ErrorBoundary from './components/ErrorBoundary.tsx';

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Listen for auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Check if user has admin role
                try {
                    const idTokenResult = await user.getIdTokenResult();
                    const hasAdminRole = !!idTokenResult.claims.adminRole;

                    if (!hasAdminRole) {
                        console.warn('User authenticated but has no admin role');
                        await signOut(auth);
                        setIsLoggedIn(false);
                    } else {
                        // User has admin role - ensure JWT is available
                        const { getStoredJWT, refreshJWTFromFirebase } = await import('./services/adminAuthService');
                        const existingToken = getStoredJWT();

                        if (!existingToken) {
                            // No JWT in session - refresh from Firebase
                            try {
                                await refreshJWTFromFirebase();
                                console.log('✅ JWT refreshed from Firebase auth');
                            } catch (error) {
                                console.error('Failed to refresh JWT:', error);
                                // Continue anyway - API calls will attempt refresh
                            }
                        }

                        setIsLoggedIn(true);
                    }
                } catch (error) {
                    console.error('Error checking admin role:', error);
                    setIsLoggedIn(false);
                }
            } else {
                setIsLoggedIn(false);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleLoginSuccess = () => {
        setIsLoggedIn(true);
        navigate('/dashboard');
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setIsLoggedIn(false);
            navigate('/');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleNavigateToSubscriptions = () => {
        navigate('/subscriptions');
    };

    const handleNavigateToActivity = () => {
        navigate('/activity');
    };

    const handleNavigateToUsers = () => {
        navigate('/users');
    };

    const handleNavigateToAdmins = () => {
        navigate('/admins');
    };

    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };

    // Show loading spinner while checking auth state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <Routes>
                {/* Public Routes */}
                <Route
                    path="/"
                    element={
                        isLoggedIn ? (
                            <Navigate to="/dashboard" replace />
                        ) : (
                            <AdminLoginPage onLoginSuccess={handleLoginSuccess} />
                        )
                    }
                />
                <Route path="/register" element={<AdminRegistration />} />

                {/* Protected Routes */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <AdminDashboard
                                onNavigateToSubscriptions={handleNavigateToSubscriptions}
                                onNavigateToActivity={handleNavigateToActivity}
                                onNavigateToUsers={handleNavigateToUsers}
                                onNavigateToAdmins={handleNavigateToAdmins}
                                onLogout={handleLogout}
                            />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/subscriptions"
                    element={
                        <ProtectedRoute requiredRole="ops_manager">
                            <AdminSubscriptionDashboard onBack={handleBackToDashboard} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/activity"
                    element={
                        <ProtectedRoute>
                            <AdminActivityDashboard onBack={handleBackToDashboard} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/users"
                    element={
                        <ProtectedRoute>
                            <AdminUserManagement onBack={handleBackToDashboard} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admins"
                    element={
                        <ProtectedRoute requiredRole="super_admin">
                            <AdminTeamManagement onBack={handleBackToDashboard} />
                        </ProtectedRoute>
                    }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </ErrorBoundary>
    );
}

export default App;